import React, { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Users, CheckCircle2, ArrowRight } from "lucide-react";

const SPACES = [
  { key: "standard", label: "Standard 4×6", price: "$1,300 / drop", note: "2 available per card" },
  { key: "basic", label: "Basic 4×3", price: "$900 / drop", note: "9 available per card" },
];

const EMPTY = { referred_name: "", contact_name: "", contact_email: "", contact_phone: "", ad_size: "", notes: "" };

export default function ReferPage() {
  const [sp] = useSearchParams();
  const zip = sp.get("zip") || "";
  const city = sp.get("city") || "";
  const state = (sp.get("state") || "").toUpperCase();
  const referrer = sp.get("ref") || "";
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const plansUrl = `/mailer?zip=${encodeURIComponent(zip)}&city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}`;
  const locLine = [city, state].filter(Boolean).join(", ") + (zip ? ` ${zip}` : "");

  const submit = async () => {
    if (!form.referred_name.trim()) return toast.error("Enter the business name you're referring");
    setBusy(true);
    try {
      await api.post("/referrals", { ...form, zip, city, state_abbr: state, referrer });
      setDone(true);
      toast.success("Referral sent — thank you!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit referral");
    } finally {
      setBusy(false);
    }
  };

  const referAnother = () => { setForm(EMPTY); setDone(false); };

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12" data-testid="refer-page">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-5 w-5 text-sam-crimson" />
        <span className="text-xs font-bold uppercase tracking-widest text-sam-crimson">Refer a trusted business</span>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-sam-navy">
        Fill your card with neighbors you trust
      </h1>
      <p className="text-sam-slate mt-3 text-base">
        {referrer ? <><span className="font-semibold text-sam-navy">{referrer}</span>, thanks for </> : "Thanks for "}
        joining the Saving America Money co-op mailer{locLine ? <> in <span className="font-semibold text-sam-navy">{locLine}</span></> : ""}.
        The same 9×12 postcard reaches 10,000 homes and carries 11 more local spots — refer businesses you trust and
        we'll invite them onto your card so everyone shares the cost.
      </p>

      <div className="grid sm:grid-cols-2 gap-3 mt-6">
        {SPACES.map((s) => (
          <div key={s.key} className="rounded-xl border border-border bg-card p-4" data-testid={`space-${s.key}`}>
            <div className="font-display font-bold text-sam-navy">{s.label}</div>
            <div className="text-sam-crimson font-semibold">{s.price}</div>
            <div className="text-xs text-sam-slate mt-0.5">{s.note}</div>
          </div>
        ))}
      </div>

      {done ? (
        <div className="mt-8 rounded-2xl border border-sam-green/40 bg-sam-green/5 p-8 text-center" data-testid="refer-success">
          <CheckCircle2 className="h-10 w-10 text-sam-green mx-auto mb-3" />
          <h2 className="font-display text-xl font-bold text-sam-navy">Referral received — thank you!</h2>
          <p className="text-sam-slate text-sm mt-1">Our team will reach out to them about a spot on your card.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-5">
            <Button variant="outline" onClick={referAnother} data-testid="refer-another-btn">Refer another business</Button>
            <Link to={plansUrl}>
              <Button className="bg-sam-crimson hover:bg-sam-crimson/90" data-testid="refer-buy-btn">
                Buy a spot now <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="mt-8 rounded-2xl border border-border bg-card p-6 space-y-4" data-testid="refer-form">
          <div>
            <Label htmlFor="ref-name">Business you're referring *</Label>
            <Input id="ref-name" data-testid="refer-business-name" value={form.referred_name}
              onChange={(e) => set("referred_name", e.target.value)} placeholder="e.g. Ozark Plumbing Co." />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ref-contact">Contact name</Label>
              <Input id="ref-contact" data-testid="refer-contact-name" value={form.contact_name}
                onChange={(e) => set("contact_name", e.target.value)} placeholder="Owner / manager" />
            </div>
            <div>
              <Label htmlFor="ref-phone">Contact phone</Label>
              <Input id="ref-phone" data-testid="refer-contact-phone" value={form.contact_phone}
                onChange={(e) => set("contact_phone", e.target.value)} placeholder="(555) 555-5555" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ref-email">Contact email</Label>
              <Input id="ref-email" data-testid="refer-contact-email" type="email" value={form.contact_email}
                onChange={(e) => set("contact_email", e.target.value)} placeholder="owner@business.com" />
            </div>
            <div>
              <Label>Suggested space</Label>
              <Select value={form.ad_size} onValueChange={(v) => set("ad_size", v)}>
                <SelectTrigger data-testid="refer-ad-size"><SelectValue placeholder="Any available" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard 4×6 — $1,300</SelectItem>
                  <SelectItem value="basic">Basic 4×3 — $900</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="ref-notes">Why you trust them (optional)</Label>
            <Textarea id="ref-notes" data-testid="refer-notes" value={form.notes}
              onChange={(e) => set("notes", e.target.value)} placeholder="A quick note helps our team make the intro." />
          </div>
          <Button className="w-full bg-sam-crimson hover:bg-sam-crimson/90" onClick={submit} disabled={busy} data-testid="refer-submit-btn">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</> : "Send referral"}
          </Button>
          <p className="text-center text-xs text-sam-slate">
            Prefer to buy a spot directly? <Link to={plansUrl} className="text-sam-crimson font-semibold underline" data-testid="refer-plans-link">See plans & pricing</Link>
          </p>
        </div>
      )}
    </div>
  );
}
